import { useMemo } from 'react';
import type { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useCardCategoryContext } from '@apps/card/components/CardCategoryProvider';
import { buildGalleryFilterHref } from './buildGalleryFilterHref';
import type { GalleryFacet, GalleryFormatParam } from './buildGalleryFilterHref';
import { MEGA_NAV_SLOTS } from './megaNav.config';
import type { MegaNavSlotId, MegaNavTarget } from './megaNav.config';
import { useMegaNavLabels } from './useMegaNavLabels';

export type ResolvedMegaNavLink = Readonly<{
  id: string;
  label: string;
  href: string;
  /** SPA route (render with router `Link`) vs. a full-page anchor. */
  isSpaRoute: boolean;
  category?: StationeryTemplateCategoryEnum;
  facet?: GalleryFacet;
  format?: GalleryFormatParam;
}>;

export type ResolvedMegaNavColumn = Readonly<{
  id: string;
  heading: string;
  links: ReadonlyArray<ResolvedMegaNavLink>;
}>;

export type ResolvedMegaNavTile = ResolvedMegaNavLink & Readonly<{ imageSrc: string }>;

export type ResolvedMegaNavSlot = Readonly<{
  id: MegaNavSlotId;
  label: string;
  /** Top-level destination; absent for grouped slots. */
  link?: ResolvedMegaNavLink;
  /** Translated "All <category>" phrase for the slot's own destination; absent for grouped slots. */
  allLabel?: string;
  isActive: boolean;
  columns: ReadonlyArray<ResolvedMegaNavColumn>;
  tiles?: ReadonlyArray<ResolvedMegaNavTile>;
}>;

const GALLERY_FORMAT_PARAMS: ReadonlyArray<GalleryFormatParam> = ['paper', 'premiumDigital', 'all'];
const isGalleryFormatParam = (value: string | undefined): value is GalleryFormatParam => GALLERY_FORMAT_PARAMS.includes(value as GalleryFormatParam);

/**
 * Public visitors have no event yet. `/event/edit/…` is the `EventRedirect` route: it sends a
 * logged-out visitor to log in or sign up, then picks their event and forwards the sub-path. A bare
 * `/edit/…` path has no route and renders NotFound (the value props' absolute links use this form too).
 */
const PUBLIC_SAMPLE_PACK_HREF = '/event/edit/cards/sample-pack';

/**
 * Admin routes outside the cards app. Embedded: the event's own `/<handle>/edit/<path>`. Public:
 * the target's `publicHref` when it has one, else the `/event/edit/<path>` redirect described above.
 */
const resolveAdminRoute = (target: Readonly<{ path: string; publicHref?: string }>, eventHandle: string | undefined): Pick<ResolvedMegaNavLink, 'href' | 'isSpaRoute'> =>
  eventHandle ? { href: `/${eventHandle}/edit/${target.path}`, isSpaRoute: true } : { href: target.publicHref ?? `/event/edit/${target.path}`, isSpaRoute: false };

/**
 * Turns the static `MEGA_NAV_SLOTS` config into render-ready slots: hrefs for the
 * current shop (public vs. admin-embedded), translated labels, and active state.
 */
export const useResolvedMegaNav = (): ReadonlyArray<ResolvedMegaNavSlot> => {
  const { getShopPath, getSamplePackPath, getEnvelopesPath, eventHandle } = useCardsRouterContext();
  const { currentCategory } = useCardCategoryContext();
  const { getValueString } = useQueryParamHelper();
  const getLabel = useMegaNavLabels();
  const [isEnvelopeProductEnabled] = useIsFeatureEnabled('printEnvelopeProduct');

  // Top-level slot links keep the visitor's current `format` (as the flat CategoryNav does);
  // deep links set their own.
  const currentFormat = getValueString('format');
  const preservedFormat = isGalleryFormatParam(currentFormat) ? currentFormat : undefined;

  return useMemo(() => {
    const resolveTarget = (target: MegaNavTarget, options: { preserveFormat?: boolean } = {}): Omit<ResolvedMegaNavLink, 'id' | 'label'> => {
      switch (target.kind) {
        case 'gallery': {
          const format = target.format ?? (options.preserveFormat ? preservedFormat : undefined);
          return {
            href: buildGalleryFilterHref(getShopPath('wedding', target.category), { facet: target.facet, format }),
            isSpaRoute: true,
            category: target.category,
            facet: target.facet,
            format,
          };
        }
        case 'samplePack':
          return eventHandle ? { href: getSamplePackPath(), isSpaRoute: true } : { href: PUBLIC_SAMPLE_PACK_HREF, isSpaRoute: false };
        // SPA either way: `getEnvelopesPath()` returns the public `/cards/envelopes` mount when
        // there is no event, and that mount offers sign-in itself.
        case 'envelopes':
          return { href: getEnvelopesPath(), isSpaRoute: true };
        case 'adminRoute':
          return resolveAdminRoute(target, eventHandle);
        case 'href':
          return { href: target.href, isSpaRoute: false };
      }
    };

    /**
     * Whether a target's destination is live for this viewer. Only flag-gated kinds answer false;
     * a second one adds a clause here rather than more logic in the mapping below.
     */
    const isTargetEnabled = (target: MegaNavTarget) => target.kind !== 'envelopes' || isEnvelopeProductEnabled;

    return MEGA_NAV_SLOTS.map<ResolvedMegaNavSlot>(slot => ({
      id: slot.id,
      label: getLabel(slot.label),
      link: slot.target ? { id: slot.id, label: getLabel(slot.label), ...resolveTarget(slot.target, { preserveFormat: true }) } : undefined,
      allLabel: slot.allLabel ? getLabel(slot.allLabel) : undefined,
      isActive: slot.activeForCategories.includes(currentCategory),
      tiles: slot.tiles?.map(tile => ({ id: tile.id, label: getLabel(tile.label), imageSrc: tile.imageSrc, ...resolveTarget(tile.target) })),
      columns: slot.columns.map(column => ({
        id: column.id,
        heading: getLabel(column.heading),
        links: column.links.filter(link => isTargetEnabled(link.target)).map(link => ({ id: link.id, label: getLabel(link.label), ...resolveTarget(link.target) })),
      })),
    }));
  }, [currentCategory, eventHandle, getEnvelopesPath, getLabel, getSamplePackPath, getShopPath, isEnvelopeProductEnabled, preservedFormat]);
};
